/**
 * Pick existing Contacts and enroll them into a campaign in one shot.
 * Shows the org's full contact list with a checkbox per row; rows that
 * are already enrolled in this campaign are visually marked +
 * checkbox is disabled (operator can see "already in") so they don't
 * accidentally re-submit them.
 */
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { worksitesOf } from "@/components/worksites";
import { ApiPaths, api } from "@/lib/api";
import { displayPhone } from "@/lib/phone";
import type { CampaignMembership, Contact, Paginated } from "@/lib/types";


export function BulkAttachBeneficiariesModal({
  campaignId,
  onClose,
}: {
  campaignId: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [checked, setChecked] = useState<Set<string>>(new Set());

  // The list is paged at 30, so typing straight through to a name would
  // fire a request per keystroke against an endpoint that now also
  // reports a total. Settle first.
  const [query, setQuery] = useState("");
  useEffect(() => {
    const id = setTimeout(() => setQuery(search.trim()), 250);
    return () => clearTimeout(id);
  }, [search]);

  // Reported 2026-09-27: the modal showed one page and nothing else, so
  // "a few records" read as the operator's entire book of beneficiaries.
  // It now pages, and says how many there are in total — a picker that
  // silently truncates is worse than a long one, because you stop
  // looking for the person who is actually there.
  const contacts = useInfiniteQuery({
    queryKey: ["contacts-for-bulk-attach", query],
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const res = await api.get<Paginated<Contact>>(ApiPaths.contacts, {
        params: {
          page: String(pageParam),
          ...(query ? { q: query } : {}),
        },
      });
      return res.data;
    },
    getNextPageParam: (last, _all, lastParam) =>
      last.next ? (lastParam as number) + 1 : undefined,
  });

  const rows = useMemo(
    () => (contacts.data?.pages ?? []).flatMap((p) => p.results ?? []),
    [contacts.data],
  );
  const total = contacts.data?.pages[0]?.count ?? 0;

  const existingMemberships = useQuery({
    queryKey: ["campaign-leads-enrolled", campaignId],
    queryFn: async () => {
      // EVERY membership, not the first 200. The "already in" lock is the
      // whole reason an enrolled beneficiary stays visible, so a partial
      // set makes the modal assert something false about members 201+.
      // The first response carries `count`, so the remaining pages go out
      // together rather than one after another.
      const size = 200;
      const first = await api.get<Paginated<CampaignMembership>>(
        ApiPaths.campaignLeads(campaignId),
        { params: { page_size: String(size), page: "1" } },
      );
      const pages = Math.ceil((first.data.count ?? 0) / size);
      if (pages <= 1) return first.data.results ?? [];
      const rest = await Promise.all(
        Array.from({ length: pages - 1 }, (_, i) =>
          api.get<Paginated<CampaignMembership>>(
            ApiPaths.campaignLeads(campaignId),
            { params: { page_size: String(size), page: String(i + 2) } },
          ),
        ),
      );
      return [
        ...(first.data.results ?? []),
        ...rest.flatMap((r) => r.data.results ?? []),
      ];
    },
  });

  const enrolledContactIds = useMemo(() => {
    const ids = new Set<string>();
    for (const m of existingMemberships.data ?? []) {
      ids.add(m.contact);
    }
    return ids;
  }, [existingMemberships.data]);

  const attach = useMutation({
    mutationFn: async () => {
      const res = await api.post<{
        enrolled: number;
        already_enrolled: number;
        skipped: Array<{ contact_id: string; reason: string }>;
        missing: string[];
      }>(ApiPaths.campaignMembershipsBulkAttach(campaignId), {
        contact_ids: [...checked],
      });
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ["campaign-leads", campaignId],
      });
      void qc.invalidateQueries({ queryKey: ["campaign", campaignId] });
      onClose();
    },
  });

  function toggle(id: string) {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t(
        "leads.bulkAttach.title",
        "Add beneficiaries to this campaign",
      )}
      className="modal-backdrop bg-black/70"
    >
      <div className="modal-panel flex w-full max-w-2xl flex-col gap-3 max-h-[90vh]">
        <header className="space-y-1">
          <h2 className="text-lg font-semibold">
            {t(
              "leads.bulkAttach.title",
              "Add beneficiaries to this campaign",
            )}
          </h2>
          <p className="text-xs text-nb-text-3">
            {t(
              "leads.bulkAttach.hint",
              "Pick existing beneficiaries to enroll. Already-enrolled ones are checked + locked.",
            )}
          </p>
        </header>

        <input
          type="search"
          className="input"
          placeholder={t("contacts.searchPlaceholder", "Search…")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <div className="flex-1 overflow-y-auto">
          {contacts.isLoading && (
            <p className="text-xs text-nb-text-3">{t("common.loading")}</p>
          )}
          {!contacts.isLoading && rows.length === 0 && (
            <p className="text-xs text-nb-text-3">
              {t("leads.bulkAttach.empty", "No beneficiaries found.")}
            </p>
          )}
          {/* Cards rather than a divided list of checkboxes. Choosing who
              to enrol is a judgement call — whether this is the right
              person, whether they are already in other campaigns, how
              many sites they carry (which now drives what the call
              bills). A one-line row showed a name and an email and made
              the operator open another tab to answer any of that. */}
          {rows.length > 0 && (
            <ul className="grid gap-2 sm:grid-cols-2">
              {rows.map((c) => {
                const enrolled = enrolledContactIds.has(c.id);
                const picked = enrolled || checked.has(c.id);
                const name =
                  (c.is_business && c.legal_name
                    ? c.legal_name
                    : `${c.first_name} ${c.last_name}`.trim()) || "—";
                const initials =
                  (c.first_name?.[0] ?? "") + (c.last_name?.[0] ?? "") ||
                  name[0] ||
                  "?";
                const sites = worksitesOf(c.addresses ?? []).length;
                return (
                  <li key={c.id}>
                    <label
                      className={`flex h-full cursor-pointer gap-2.5 rounded-lg border p-2.5 transition ${
                        enrolled
                          ? "cursor-not-allowed border-nb-mint/40 bg-nb-mint/5"
                          : picked
                            ? "border-nb-mint bg-nb-mint/10"
                            : "border-nb-border bg-nb-surface/40 hover:border-nb-text-3"
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="mt-0.5 shrink-0"
                        checked={picked}
                        disabled={enrolled}
                        onChange={() => !enrolled && toggle(c.id)}
                      />
                      <span
                        aria-hidden="true"
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-nb-surface-2 text-[11px] font-semibold uppercase text-nb-text-2 ring-1 ring-nb-border"
                      >
                        {initials}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-1.5">
                          <span className="truncate text-sm font-medium text-nb-text">
                            {name}
                          </span>
                          {enrolled && (
                            <span className="shrink-0 rounded bg-nb-mint/20 px-1.5 py-0.5 text-[10px] uppercase text-nb-mint">
                              {t("leads.bulkAttach.alreadyInBadge")}
                            </span>
                          )}
                        </span>
                        <span className="mt-0.5 block truncate text-[11px] text-nb-text-3">
                          {c.email || displayPhone(c.phone) || "—"}
                        </span>
                        {/* The signals that actually decide the pick. */}
                        <span className="mt-1 flex flex-wrap gap-1">
                          {c.is_business && (
                            <span className="rounded border border-nb-border px-1.5 py-0.5 text-[10px] text-nb-text-3">
                              {t("contacts.businessHeading")}
                            </span>
                          )}
                          {sites > 0 && (
                            <span className="rounded border border-nb-border px-1.5 py-0.5 text-[10px] text-nb-text-3">
                              {t("leads.bulkAttach.siteCount", { count: sites })}
                            </span>
                          )}
                          {(c.membership_count ?? 0) > 0 && (
                            <span className="rounded border border-nb-border px-1.5 py-0.5 text-[10px] text-nb-text-3">
                              {t("leads.bulkAttach.campaignCount", {
                                count: c.membership_count,
                              })}
                            </span>
                          )}
                        </span>
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}

          {/* Say how many there are, and offer the rest. Without this the
              first page reads as the whole book. */}
          {rows.length > 0 && (
            <div className="mt-3 flex items-center justify-between gap-2">
              <span className="text-[11px] text-nb-text-3">
                {t("leads.bulkAttach.showing", {
                  shown: rows.length,
                  total,
                })}
              </span>
              {contacts.hasNextPage && (
                <button
                  type="button"
                  className="btn-ghost text-xs"
                  disabled={contacts.isFetchingNextPage}
                  onClick={() => void contacts.fetchNextPage()}
                >
                  {contacts.isFetchingNextPage
                    ? t("common.loading")
                    : t("leads.bulkAttach.loadMore", "Load more")}
                </button>
              )}
            </div>
          )}
        </div>

        {attach.isError && (
          <p className="text-xs text-cvp-danger">{String(attach.error)}</p>
        )}

        <footer className="flex items-center justify-between gap-2 pt-2">
          <span className="text-xs text-nb-text-3">
            {checked.size}{" "}
            {t("leads.bulkAttach.selected", "selected")}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              className="btn-ghost text-sm"
              onClick={onClose}
            >
              {t("common.cancel", "Cancel")}
            </button>
            <button
              type="button"
              className="btn-primary text-sm"
              disabled={checked.size === 0 || attach.isPending}
              onClick={() => attach.mutate()}
            >
              {attach.isPending
                ? t("common.saving", "Saving…")
                : t("leads.bulkAttach.enroll", "Enroll")}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
